import { ArrowLeft, MousePointerClick } from "lucide-react";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface VoiceSetupProps {
  onBack: () => void;
  onSwitchToClick: () => void;
}

const transcript = [
  {
    speaker: "agent" as const,
    text: "Hi! I'm here to set up your new genie. What's the main thing you want it to do?",
  },
  {
    speaker: "user" as const,
    text: "I want a genie for my dog grooming business that books appointments and answers questions.",
  },
  {
    speaker: "agent" as const,
    text: "Perfect — sounds like a mix of booking and support. How should it sound when it talks to customers? Friendly and casual, professional, or something else?",
  },
  { speaker: "user" as const, text: "Warm and friendly. We're not a big corporate place." },
  { speaker: "agent" as const, text: "Got it. Short replies, or full walkthroughs?" },
  { speaker: "user" as const, text: "Short. People are busy." },
  {
    speaker: "agent" as const,
    text: "Locking that in. One last thing — anything customers should never hear, like specific prices or services you don't offer?",
  },
];

export const VoiceSetup = ({ onBack, onSwitchToClick }: VoiceSetupProps) => {
  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="text-sm text-gray-500 hover:text-genie-primary flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to setup mode
        </button>
        <button
          onClick={onSwitchToClick}
          className="text-sm font-medium text-genie-primary hover:underline flex items-center gap-1"
        >
          <MousePointerClick className="w-3.5 h-3.5" />
          Switch to click setup
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Mic orb / status — gradient border */}
        <div className="lg:col-span-2 p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
          <div className="rounded-[14px] bg-white p-6 md:p-8 h-full flex flex-col items-center text-center">
            <GenieIcon
              name="microphone"
              size={80}
              className="h-20 w-20 mb-4 animate-bounce-subtle"
            />
            <p className="text-xl font-bold text-[#222B45] mb-1">Listening...</p>
            <p className="text-sm text-[#64748B] mb-6">
              Talk naturally. Take your time.
            </p>

            <div className="w-full space-y-2">
              <CaptureCard label="Job" value="Booking + Support" filled />
              <CaptureCard label="Personality" value="Warm, short replies" filled />
              <CaptureCard label="Boundaries" value="Listening..." />
            </div>

            <button
              type="button"
              className="mt-6 w-full px-4 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors"
            >
              Pause conversation
            </button>
          </div>
        </div>

        {/* Transcript */}
        <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <GenieIcon name="genie-lamp" size={32} className="h-8 w-8" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Setup conversation</p>
              <p className="text-xs text-gray-500">Live transcript</p>
            </div>
          </div>

          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-2">
            {transcript.map((line, i) => (
              <div
                key={i}
                className={`text-sm leading-relaxed ${
                  line.speaker === "agent" ? "text-gray-900" : "text-gray-700"
                }`}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wider mr-2 text-genie-primary">
                  {line.speaker === "agent" ? "Setup Genie" : "You"}
                </span>
                {line.text}
              </div>
            ))}
            <div className="flex items-center gap-2 text-gray-400 text-xs italic pt-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-genie-primary animate-pulse" />
              Listening for your response...
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-gray-500 mt-6">
        Your setup genie will summarize everything at the end. You'll get to review before
        deploying.
      </p>
    </div>
  );
};

const CaptureCard = ({
  label,
  value,
  filled,
}: {
  label: string;
  value: string;
  filled?: boolean;
}) => (
  <div
    className={`rounded-lg px-3 py-2 border text-left ${
      filled ? "bg-genie-primary/5 border-genie-primary/20" : "bg-gray-50 border-dashed border-gray-300"
    }`}
  >
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-0.5">
      {label}
    </p>
    <p className={`text-xs ${filled ? "text-gray-900 font-medium" : "text-gray-400 italic"}`}>
      {value}
    </p>
  </div>
);
